import { getEnvironmentProps, isDmnURL } from "../../utils/environment";
import { dmnIdentity, performSilentReauth } from "../../utils/dmnIdentity";
import { reportApiWarningOnce, throwApiError } from "../errorHandler";
import { collinsApiHeaders } from "../getDefaultHeaders";
import { readJsonBody } from "../responseBody";
import { UserPreferences } from "../../types/userPreferences";

const ERROR_TITLE = "Error fetching user preferences";

const requestUserPreferences = (
  baseUrl: string | undefined,
  userId: string,
  token: string
): Promise<Response> =>
  fetch(`${baseUrl}/users/${userId}/preferences`, {
    method: "GET",
    headers: collinsApiHeaders(token)
  });

/**
 * Refresh the access token after a 401. The identity SDK exposes no explicit
 * refresh — on the DMN web path a silent re-auth renews the server-side
 * session the token is read from; on the native/Workspace path re-reading
 * getAccessToken lets the SDK return a renewed token.
 */
const refreshAccessToken = async (
  authApiBaseUrl: string | undefined
): Promise<string> => {
  if (isDmnURL() && authApiBaseUrl) {
    try {
      await performSilentReauth(authApiBaseUrl);
    } catch {
      // Fall through and re-read the token anyway — it may still be valid.
    }
  }

  return dmnIdentity.getAccessToken();
};

/**
 * Fetch user preferences from Collins API.
 * Returns null when the user has no saved preferences (404),
 * allowing the caller to fall back to defaults.
 * Throws on other failures.
 * @param userId - The DMN user _id (MongoDB ObjectId) to fetch preferences for.
 */
export const fetchUserPreferences = async (
  userId: string
): Promise<UserPreferences | null> => {
  let token = await dmnIdentity.getAccessToken();
  let environmentVars = getEnvironmentProps(token);

  if (!userId) {
    throw new Error("Unable to resolve user ID");
  }

  let response = await requestUserPreferences(
    environmentVars?.COLLINS_API_BASE_URL,
    userId,
    token
  );

  if (response.status === 401) {
    token = await refreshAccessToken(environmentVars?.AUTH_API_BASE_URL);
    environmentVars = getEnvironmentProps(token);
    response = await requestUserPreferences(
      environmentVars?.COLLINS_API_BASE_URL,
      userId,
      token
    );
  }

  if (response.status === 404) {
    return null;
  }

  if (response.status === 401) {
    // Token refresh did not recover access — record it once and throw
    // silently so the caller can keep defaults without an error toast.
    reportApiWarningOnce(response, ERROR_TITLE);
    await throwApiError(response, ERROR_TITLE, { silent: true });
  }

  if (!response.ok) {
    await throwApiError(response, ERROR_TITLE);
  }

  return (await readJsonBody<UserPreferences>(response)) ?? null;
};
